import { CommonModule } from '@angular/common';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  OnDestroy,
  SimpleChanges,
  computed,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbTooltip, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { filter, map, switchMap, tap } from 'rxjs';
import { FileUploadService } from '../../services/file-upload.service';
import { SvgIconsComponent } from '../svg-icons/svg-icons.component';

type RecorderStatus = 'idle' | 'recording' | 'paused' | 'finished';

@Component({
  selector: 'voice-recorder-widget',
  imports: [CommonModule, NgbTooltip, SvgIconsComponent, NgbPopoverModule],
  standalone: true,
  templateUrl: './voice-recorder.component.html'
})
export class VoiceRecorderComponent implements OnDestroy {
  readonly maxDurationSeconds = input(120);
  readonly recordingSaved = output<{ s3Key: string; documentId: string }>();
  readonly removeRecording = output<void>();
  readonly resetRecording = input<boolean>(false);
  readonly isPanelOpen = signal(false);
  readonly status = signal<RecorderStatus>('idle');
  readonly timer = signal(0);
  readonly uploading = signal(false);
  readonly uploadProgress = signal(0);
  readonly alertMessage = signal<string | null>(null);
  readonly canRetryPermission = signal(false);
  readonly canCheckAgain = signal(false);
  readonly audioUrl = model<string | null>(null);

  readonly formattedTime = computed(() => this.#formatDuration(this.timer()));
  readonly allowedDurationLabel = computed(() => this.#formatDuration(this.#maxDuration()));
  readonly showPreview = computed(() => this.audioUrl() !== null);

  #fileUploadService = inject(FileUploadService);
  #destroyRef = inject(DestroyRef);
  #mediaRecorder?: MediaRecorder;
  #recordedChunks: BlobPart[] = [];
  #audioBlob: Blob | null = null;
  #timerRef: number | null = null;
  #stream?: MediaStream;
  #objectUrl?: string;
  readonly #appUrl = typeof window !== 'undefined' ? window.location.origin : 'this site';

  ngOnChanges(changes: SimpleChanges): void {
    this.clearRecording();
    this.audioUrl.set(changes['audioUrl']?.currentValue ?? null);
    this.isPanelOpen.set(false);
  }

  togglePanel(): void {
    const next = !this.isPanelOpen();
    this.isPanelOpen.set(next);
    if (!next) {
      this.#mediaRecorder?.pause();
      this.status.set('paused');

      // this.stopRecording();
      // this.clearRecording();
      this.alertMessage.set(null);
      this.canRetryPermission.set(false);
    }
  }

  async startRecording(): Promise<void> {
    if (this.uploading() || this.status() === 'recording') return;

    const stream = await this.#requestStream();
    if (!stream) return;

    this.#stream = stream;
    this.alertMessage.set(null);
    this.canRetryPermission.set(false);
    this.#resetObjectUrl();
    this.audioUrl.set(null);
    this.timer.set(0);

    this.#recordedChunks = [];
    const recorder = new MediaRecorder(stream);
    this.#mediaRecorder = recorder;

    recorder.ondataavailable = event => {
      if (event.data?.size) {
        this.#recordedChunks.push(event.data);
      }
    };

    recorder.onstop = () => {
      if (!this.#recordedChunks.length) {
        this.status.set('idle');
        return;
      }

      const blob = new Blob(this.#recordedChunks, { type: recorder.mimeType || 'audio/webm' });
      this.#audioBlob = blob;
      const url = URL.createObjectURL(blob);
      this.#objectUrl = url;
      this.audioUrl.set(url);
      this.status.set('finished');
      this.#recordedChunks = [];
    };

    recorder.start();
    this.status.set('recording');
    this.#startTimer();
  }

  pauseOrResume(): void {
    if (!this.#mediaRecorder) return;

    if (this.status() === 'recording' && this.#mediaRecorder.state === 'recording') {
      this.#mediaRecorder.pause();
      this.status.set('paused');
      this.#fileUploadService.recording = true;
      return;
    }

    if (this.status() === 'paused' && this.#mediaRecorder.state === 'paused') {
      this.#mediaRecorder.resume();
      this.status.set('recording');
    }
  }

  stopRecording(): void {
    this.#fileUploadService.recording = true;
    if (!this.#mediaRecorder || this.#mediaRecorder.state === 'inactive') {
      this.#stopTimer();
      this.#releaseStream();
      return;
    }

    this.#mediaRecorder.stop();
    this.#stopTimer();
    this.#releaseStream();
  }

  clearRecording(): void {
    this.#resetObjectUrl();
    this.#audioBlob = null;
    this.audioUrl.set(null);
    this.timer.set(0);
    this.status.set('idle');
    this.removeRecording.emit();
    this.#fileUploadService.recording = false;
  }

  async checkPermissionAgain(): Promise<void> {
    // Clear alert messages
    this.alertMessage.set(null);
    this.canRetryPermission.set(false);
    this.canCheckAgain.set(false);

    // Re-check permission state
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (result.state === 'granted') {
          // Permission is now granted, start recording
          await this.startRecording();
          return;
        } else if (result.state === 'denied') {
          this.alertMessage.set(
            `Microphone permission is still blocked. Please enable it manually: Click the lock icon (🔒) in your browser's address bar, find "Microphone" and change it to "Allow", then click "Check again" below.`
          );
          this.canCheckAgain.set(true);
          return;
        }
      }
    } catch {
      // Permissions API not available
    }

    // If permission state is "prompt" or unknown, try to request it
    await this.retryPermissionRequest();
  }

  async retryPermissionRequest(): Promise<void> {
    // Clear alert messages
    this.alertMessage.set(null);
    this.canRetryPermission.set(false);
    this.canCheckAgain.set(false);

    // Check if getUserMedia is available
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.alertMessage.set('Recording is not supported in this browser.');
      this.canRetryPermission.set(false);
      return;
    }

    // Ensure any existing stream is released
    this.#releaseStream();

    // Reset status to idle to allow recording to start
    if (this.status() !== 'idle') {
      this.status.set('idle');
    }

    // Check current permission state first
    let permissionState: PermissionState | null = null;
    let permissionStatus: PermissionStatus | null = null;
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        permissionState = result.state;
        permissionStatus = result;

        // If already granted, start recording
        if (permissionState === 'granted') {
          await this.startRecording();
          return;
        }
      }
    } catch {
      // Permissions API not available, continue with getUserMedia
    }

    // Try Permissions API request() method first (Chrome/Edge only, limited support)
    // This is the most direct way to request permission programmatically
    if (permissionState === 'prompt' && navigator.permissions) {
      try {
        // Type assertion needed as request() is not in standard TypeScript types yet
        const permissionsRequest = (navigator.permissions as any).request;
        if (typeof permissionsRequest === 'function') {
          const result = await permissionsRequest({ name: 'microphone' });
          if (result.state === 'granted') {
            await this.startRecording();
            return;
          }
          // Update permission state after request
          permissionState = result.state;
        }
      } catch (error: any) {
        // Permissions API request() not supported or failed, continue with getUserMedia
        console.debug('Permissions API request() not available:', error);
      }
    }

    // If permanently denied, we cannot request it programmatically
    // JavaScript cannot change browser permission settings - this is a security restriction
    if (permissionState === 'denied') {
      this.alertMessage.set(
        `Microphone permission is permanently blocked. Please enable it manually: Click the lock icon (🔒) in your browser's address bar, find "Microphone" and change it to "Allow", then click "Check again" below.`
      );
      this.canRetryPermission.set(false);
      this.canCheckAgain.set(true);
      return;
    }

    // Permission is "prompt" or unknown - try to request it via getUserMedia
    // This will trigger the browser's permission popup
    // Note: This must be called directly from user interaction to work
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Permission granted - stop the test stream and start recording
      stream.getTracks().forEach(track => track.stop());
      await this.startRecording();
      return;
    } catch (error: any) {
      const isPermissionDenied = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError';

      if (isPermissionDenied) {
        // Check permission state again after denial
        try {
          if (navigator.permissions && navigator.permissions.query) {
            const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            if (result.state === 'denied') {
              this.alertMessage.set(
                `Microphone permission was denied and is now blocked. Please enable it manually: Click the lock icon (🔒) in your browser's address bar, find "Microphone" and change it to "Allow", then click "Check again" below.`
              );
              this.canRetryPermission.set(false);
              this.canCheckAgain.set(true);
              return;
            }
          }
        } catch {
          // Permissions API not available
        }

        // Permission was denied but might still be in "prompt" state
        // Show message but allow retry
        this.alertMessage.set(
          `Microphone permission was denied. Please allow access when prompted, or enable it in your browser settings.`
        );
        this.canRetryPermission.set(true);
      } else {
        this.alertMessage.set(`Error accessing microphone: ${error?.message || 'Unknown error'}`);
        this.canRetryPermission.set(false);
      }
    }
  }

  uploadRecording(): void {
    if (!this.#audioBlob || this.uploading()) return;

    const fileName = `voice-recording-${Date.now()}.webm`;
    const file = new File([this.#audioBlob], fileName, { type: this.#audioBlob.type || 'audio/webm' });

    this.uploading.set(true);
    this.uploadProgress.set(0);
    this.alertMessage.set(null);
    this.canRetryPermission.set(false);

    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(fileObject =>
          this.#fileUploadService.uploadFileToS3WithProgress(fileObject).pipe(
            tap(event => this.#handleUploadEvent(event)),
            filter(event => event?.type === HttpEventType.Response),
            map(() => ({ s3Key: fileObject.s3Key, documentId: fileObject.documentId }))
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: s3Key => {
          this.uploading.set(false);
          this.uploadProgress.set(0);
          this.recordingSaved.emit({ s3Key: s3Key.s3Key, documentId: s3Key.documentId });
          this.togglePanel();
        },
        error: () => {
          this.uploading.set(false);
          this.alertMessage.set('Unable to upload recording right now. Please try again.');
          this.canRetryPermission.set(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.stopRecording();
    this.#resetObjectUrl();
  }

  #handleUploadEvent(event: HttpEvent<unknown>): void {
    if (!event) return;

    if (event.type === HttpEventType.UploadProgress) {
      const total = event.total ?? 0;
      const progress = total > 0 ? Math.round((event.loaded * 100) / total) : 0;
      this.uploadProgress.set(progress);
    }

    if (event.type === HttpEventType.Response) {
      this.uploadProgress.set(100);
    }
  }

  async #requestStream(): Promise<MediaStream | null> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.alertMessage.set('Recording is not supported in this browser.');
      this.canRetryPermission.set(false);
      return null;
    }

    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.alertMessage.set(`You need to allow recording permission to '${this.#appUrl}' so that recording can begin.`);
      this.canRetryPermission.set(true);
      return null;
    }
  }

  #startTimer(): void {
    if (this.#timerRef !== null) return;

    this.#timerRef = window.setInterval(() => {
      if (this.status() !== 'recording') return;

      const limit = this.#maxDuration();
      const nextValue = this.timer() + 1;

      if (nextValue >= limit) {
        this.timer.set(limit);
        this.stopRecording();
        this.alertMessage.set(`Maximum recording length of ${this.allowedDurationLabel()} reached.`);
        this.canRetryPermission.set(false);
        return;
      }

      this.timer.set(nextValue);
    }, 1000);
  }

  #stopTimer(): void {
    if (this.#timerRef === null) return;
    clearInterval(this.#timerRef);
    this.#timerRef = null;
  }

  #releaseStream(): void {
    this.#stream?.getTracks().forEach(track => track.stop());
    this.#stream = undefined;
  }

  #resetObjectUrl(): void {
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = undefined;
    }
    this.#fileUploadService.recording = false;
  }

  #maxDuration(): number {
    const value = Number(this.maxDurationSeconds());
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 120;
  }

  #formatDuration(totalSeconds: number): string {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}
